import React, { type MutableRefObject, useMemo, useRef, useState } from "react";

import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedIcon from "@mui/icons-material/Verified";
import { SubscriptionPlans } from "@prisma/client";
import clsx from "clsx";

import AudioPlayer, {
  type AudioPlayerRef,
} from "@musicfy/components//AudioPlayer";
import Button from "@musicfy/components/Button";
import Dialog from "@musicfy/components/Dialog";
import Icon from "@musicfy/components/Icon";
import ModelThumbnail from "@musicfy/components/ModelThumbnail";
import StartCheckoutFlowButton from "@musicfy/components/PaymentButtons/StartCheckoutFlowButton";
import { getNextSubscriptionTier } from "@musicfy/contants/Subscriptions";
import { useToggleFavoriteModel } from "@musicfy/libs/ModelsProvider/hooks";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { capitalize, formatNumberAsString } from "@musicfy/utils";
import useIsVisible from "@musicfy/utils/hooks/useVisible";

const ModelCard: React.FC<{
  model: IModel;
  onSelectModel: (model: IModel) => void;
  isSelected: boolean;
  audioPlayersRef: MutableRefObject<AudioPlayerRef[]>;
  modelIndex: number;
  isLocked: boolean;
  isTrending: boolean;
}> = ({
  model,
  onSelectModel,
  isSelected,
  audioPlayersRef,
  modelIndex,
  isLocked,
  isTrending,
}) => {
  const toggleFavoriteModel = useToggleFavoriteModel();
  const cardRef = useRef(null);
  const isVisible = useIsVisible(cardRef);
  const [isLockDialogOpen, setIsLockDialogOpen] = useState(false);

  const isModelSaved = useMemo(() => {
    return model.isFavorited;
  }, [model.isFavorited]);

  const isNewModel =
    new Date(model.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;

  const { subscription } = useSubscriptionContext();

  const usageCount = model.usageCount || 0;

  const nextSubscription =
    getNextSubscriptionTier(subscription ?? null) || SubscriptionPlans.studio;

  return (
    <>
      <div ref={cardRef}>
        <Dialog
          open={isLockDialogOpen}
          onClose={() => setIsLockDialogOpen(false)}
          width="450px"
          TitleIcon={<Icon name="crown" />}
          title="Upgrade Needed To Unlock"
          subtitle={
            nextSubscription === "studio"
              ? "Limit Reached"
              : `Get access to all Musicfy & Community voices by upgrading to the ${capitalize(
                  nextSubscription
                )} Plan.`
          }
          primaryButton={
            <StartCheckoutFlowButton
              origin="locked-voice"
              fullWidth
              className="!capitalize"
            >
              Upgrade Now
            </StartCheckoutFlowButton>
          }
          secondaryButton={
            <Button
              onClick={() => setIsLockDialogOpen(false)}
              fullWidth
              style="tertiary"
            >
              Cancel
            </Button>
          }
        />
        {isVisible ? (
          <div
            role="button"
            aria-label={`${model.artist}`}
            tabIndex={0}
            onClick={() => {
              if (isLocked) {
                setIsLockDialogOpen(true);
                return;
              }

              onSelectModel(model);
            }}
            className={clsx(
              "w-full relative overflow-hidden aspect-square rounded-12 border border-solid transition-all",
              {
                "border-green": isSelected,
                "border-transparent xl:hover:border-green/50": !isSelected,
              }
            )}
          >
            <div className="absolute w-full h-full">
              <div className="absolute w-full h-full bg-gradient-to-b from-black/70 via-black/20 to-black/70" />
              <ModelThumbnail
                thumbnail={model.thumbnail}
                alt={`${model.artist} thumbnail`}
                className="h-full w-full object-cover before:absolute before:content-[''] before:w-full before:h-full before:bg-gradient-radial before:from-pink-200 before:to-gray-1000 before:left-0 before:top-0"
              />
            </div>

            {isSelected && (
              <Icon
                name="check-circle"
                className="absolute text-green top-3 left-3"
              />
            )}

            {!isSelected &&
              !isTrending &&
              !model.isCommunityModel &&
              !model.isPrivate &&
              model.isCopyrightFree && (
                <VerifiedIcon className="absolute text-pink-200/80 top-3 left-3" />
              )}
            {isTrending && !isSelected && (
              <div className="absolute top-3 left-3 text-20">🔥</div>
            )}

            <div className="absolute top-3 right-3 flex items-center gap-4">
              <div className="flex items-center gap-2">
                {usageCount > 10 && (
                  <div className="text-12">
                    {formatNumberAsString(usageCount)}
                  </div>
                )}

                <div
                  role="button"
                  aria-label={`${isModelSaved ? "Remove" : "Add"} ${
                    model.artist
                  } ${isModelSaved ? "from" : "to"} favorites`}
                  tabIndex={0}
                  className="!m-0 !p-0 !text-white flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavoriteModel(model.id);
                  }}
                >
                  {isModelSaved ? (
                    <FavoriteIcon className="cursor-pointer !text-20 transition-all xl:hover:text-white text-green" />
                  ) : (
                    <FavoriteBorderIcon className="cursor-pointer !text-20 transition-all xl:hover:text-green" />
                  )}
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 w-full rounded-t-8 backdrop-blur-md bg-white/15 py-3 px-2 lg:px-4">
              <div className="flex lg:gap-4 gap-2 items-center">
                {!!model.audioPreviewUrl && (
                  <div
                    className="rounded-full w-fit"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Play ${model.artist} audio preview`}
                  >
                    <AudioPlayer
                      lazyLoad
                      type="circle"
                      onPlay={() => {
                        audioPlayersRef.current.forEach((audioPlayer, i) => {
                          if (i !== modelIndex) {
                            audioPlayer.pause();
                          }
                        });
                      }}
                      ref={(ref) => {
                        if (ref) {
                          audioPlayersRef.current[modelIndex] = ref;
                        }
                      }}
                      audioSrc={model.audioPreviewUrl}
                    />
                  </div>
                )}
                <div className="truncate">
                  <div className="flex items-center gap-1">
                    <div className="font-bold truncate">{model.artist}</div>

                    {!!model.genre && (
                      <div className="text-10 capitalize flex items-center justify-center py-1 px-2 rounded-full bg-white/15">
                        {model.genre}
                      </div>
                    )}
                  </div>
                  <div className="mt-[2px] text-white/60 text-12 truncate">
                    {model.createdBy}
                  </div>
                </div>
              </div>

              {isNewModel && (
                <div className="absolute left-3 -top-7 bg-gray-1000/40 rounded-full">
                  <Icon name="new" />
                </div>
              )}

              {isLocked && (
                <div className="absolute bg-white/25 w-8 h-8 flex items-center justify-center backdrop-blur-lg rounded-full p-2 right-3 -top-10">
                  <LockIcon className="!text-18" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="invisible absolute right-0 top-0 z-20 flex w-full max-w-max justify-end gap-3 p-4 px-6 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover/item:visible group-hover/item:opacity-100">
            &nbsp;
          </div>
        )}
      </div>
    </>
  );
};

export default ModelCard;
